/**
 * The ring (ลานประลอง): ladder bouts (บันไดประลอง, data/ladder.ts) and the daily arena ranking
 * (ประลองยุทธ, data/arena.ts). Queueing, being matched, the countdown, the fight and its result,
 * what each bout pays, the ladders' rewards and shops, and what the player may not do while in the
 * ring. The World keeps `duel`, `arenaPicks`, `arenaLast`, `inArena()`, `arenaRank()`,
 * `arenaBuild()` and `arenaNewDay()` as its public face of this system.
 */
import { BAG_SIZE, ITEMS } from '../data/items';
import { combatBlockText } from '../data/combat';
import { ARENA, ARENA_ROSTER, arenaReward, type ArenaBuild } from '../data/arena';
import { contenderStrength, eloDelta, LADDER, LADDERS, tierAt, type LadderId } from '../data/ladder';
import { getArt, getSkill } from '../data/skills';
import { arenaBody } from '../arena';
import { ARENA_GEAR_BY_ID, ARENA_GOOD_BY_ID, arenaGearRoll } from '../data/arenaShop';
import type { ActivityMode } from '../data/modes';
import { GOODS } from '../data/professions';
import { modesOf } from './modes';
import { newArena } from '../progress';
import { DUEL_GROUND, inRing } from '../data/duelGround';
import type { Duel, Entity, PlayerCommand } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** What may not be done in the ring at all (counting down, fighting or seeing the result). */
const RING_REFUSED = new Set<PlayerCommand['t']>([
  'mount',
  'meditate',
  'travel',
  'ladderJoin',
  'arenaFight',
  'pk',
]);

/** What the player may not do while an arena bout fights itself. */
const ARENA_LOCKED = new Set<PlayerCommand['t']>([
  'moveTo',
  'moveDir',
  'engage',
  'targetNearest',
  'untarget',
  'attack',
  'interact',
  'skill',
  'block',
  'sprint',
  'dash',
  'jump',
  'sheathe',
  'fly',
  'auto',
  'setArt',
  'setNeigong',
  'equip',
  'unequip',
  'questAccept',
  'questTurnIn',
]);

export class ArenaSystem implements WorldSystem {
  readonly name = 'arena';
  /** The bout under way (or the ladder queue), or null. */
  duel: Duel | null = null;
  /** Arena ranks offered to challenge, and the last bout fought. */
  picks: number[] = [];
  last: { won: boolean; foe: string; from: number; to: number } | null = null;

  constructor(private readonly h: SystemHost) {}

  private get w() {
    return this.h.world;
  }

  readonly commands = {
    arenaFight: (c: { rank: number; mode?: ActivityMode }) => this.arenaFight(c.rank, c.mode),
    arenaRefresh: () => this.arenaRefresh(),
    ladderJoin: (c: { ladder: LadderId; mode?: ActivityMode }) => this.ladderJoin(c.ladder, c.mode),
    ladderCancel: () => {
      if (this.duel?.phase === 'queue') {
        this.h.emit({ type: 'ladder', phase: 'cancel', ladder: this.duel.ladder });
        this.duel = null;
        this.h.notice('ออกจากคิวประลอง');
      }
    },
    ladderClaim: (c: { ladder: LadderId; tier: number }) => this.ladderClaim(c.ladder, c.tier),
    ladderBuy: (c: { ladder: LadderId; index: number; gear?: string; picks?: string[] }) =>
      c.gear ? this.ladderBuyGear(c.ladder, c.gear, c.picks ?? []) : this.ladderBuy(c.ladder, c.index),
  };

  /** In the ring nothing that leaves it is allowed, and an arena bout fights itself. */
  refuse(cmd: PlayerCommand): boolean {
    if (this.inArena() && RING_REFUSED.has(cmd.t)) {
      this.h.notice('อยู่ในลานประลอง');
      return true;
    }
    // An arena bout fights itself: the player only watches (and may not change their build).
    return this.inArena() && !this.duel!.ladder && !this.duel!.open && ARENA_LOCKED.has(cmd.t);
  }

  /** The player's place in today's arena ranking (1-based). */
  arenaRank(): number {
    return this.w.progress.arena.order.indexOf(-1) + 1;
  }

  /** The build of whoever holds an arena rank: the player's own, or a contender's. */
  arenaBuild(rank: number): ArenaBuild {
    const who = this.w.progress.arena.order[rank - 1];
    if (who === undefined || who >= 0) return ARENA_ROSTER[who ?? 0]!;
    const pr = this.w.progress;
    const skills = getArt(pr.activeArt).skills;
    return {
      name: pr.name,
      title: pr.title,
      sprite: this.w.player.sprite,
      art: pr.activeArt,
      skillLevel: Math.round(skills.reduce((t, id) => t + this.w.skillLevel(id), 0) / skills.length),
      neigong: pr.activeNeigong,
      neigongLevel: pr.neigong[pr.activeNeigong] ?? 1,
      equipment: { ...pr.equipment },
    };
  }

  /** Draws five to challenge: mostly from just above the player, a couple from just below. */
  private arenaRefresh(): void {
    const me = this.arenaRank();
    const n = this.w.progress.arena.order.length;
    const pool: number[] = [];
    for (let r = Math.max(1, me - 10); r <= Math.min(n, me + 2); r++) if (r !== me) pool.push(r);
    const picks: number[] = [];
    while (picks.length < ARENA.choices && pool.length)
      picks.push(pool.splice(Math.floor(this.h.random() * pool.length), 1)[0]!);
    this.picks = picks.sort((a, b) => a - b);
  }

  /**
   * Challenge an offered rank: both fighters are brought into the ring and fight on their own until
   * one falls. Beating someone above takes their place.
   */
  private arenaFight(rank: number, asked?: ActivityMode): void {
    const p = this.w.player;
    const a = this.w.progress.arena;
    if (!this.picks.length) this.arenaRefresh();
    if (!this.picks.includes(rank)) return this.h.notice('เลือกคู่ประลองจากรายชื่อ');
    if (a.attempts >= ARENA.attemptsPerDay) return this.h.notice('วันนี้ท้าประลองครบแล้ว');
    if (this.duel) return this.h.notice('อยู่ระหว่างประลองอยู่แล้ว');
    if (p.dead || p.jailT > 0) return this.h.notice('ตอนนี้ประลองไม่ได้');
    if (p.combatT > 0) return this.h.notice(combatBlockText(p));
    const b = this.arenaBuild(rank);
    const mode = modesOf(this.w)?.resolve('arena', asked) ?? 'npc';
    a.attempts++;
    const d: Duel = {
      ladder: null,
      phase: 'countdown',
      t: 0,
      foeId: -1,
      foe: null,
      back: { x: p.x, y: p.y },
      won: false,
      arenaRank: rank,
      autoWas: this.w.auto.on,
      mode,
    };
    this.duel = d;
    this.bringIn(d, { ...b, title: `อันดับ ${rank} · ${b.title}` }, rank, arenaBody(b));
    this.h.log('sys', `ประลองยุทธ: ท้า ${b.name} (อันดับ ${rank})`);
    this.h.emit({ type: 'progressChanged' });
  }

  /** An arena bout decided: the rank changes hands if the player beat someone above. */
  private endArenaBout(d: Duel, won: boolean): void {
    const a = this.w.progress.arena;
    const me = this.arenaRank();
    const rank = d.arenaRank;
    let to = me;
    if (won) {
      a.wins++;
      if (rank < me) {
        [a.order[rank - 1], a.order[me - 1]] = [a.order[me - 1]!, a.order[rank - 1]!];
        to = rank;
      }
    } else a.losses++;
    Object.assign(d, { phase: 'result', t: LADDER.resultSeconds, won });
    this.last = { won, foe: d.foe!.name, from: me, to };
    this.h.log(
      'sys',
      `ประลองยุทธ: ${won ? 'ชนะ' : 'แพ้'} ${d.foe!.name} (อันดับ ${rank})${to !== me ? ` · ขึ้นสู่อันดับ ${to}` : ''}`,
    );
    this.arenaRefresh();
    this.h.emit({ type: 'ladder', phase: 'result', ladder: null, foe: d.foe!.name, won });
    this.h.emit({ type: 'progressChanged' });
  }

  /**
   * A new day (the client's calendar date, by the server in phase 2): yesterday's rank pays its
   * reward if the player took part, and the ranking starts over. Returns what was paid, if anything.
   */
  arenaNewDay(day: string): string | null {
    const pr = this.w.progress;
    const a = pr.arena;
    if (a.day === day) return null;
    let msg: string | null = null;
    if (a.day && a.attempts > 0) {
      const rank = this.arenaRank();
      const band = arenaReward(rank);
      msg = `ประลองยุทธปิดวัน อันดับ ${rank} (${band.label}): ${this.h.grantReward(band.reward)}`;
      this.h.log('sys', msg);
    }
    pr.arena = newArena(day);
    this.picks = [];
    this.last = null;
    this.h.emit({ type: 'progressChanged' });
    return msg;
  }

  /** In the arena (counting down, fighting or seeing the result), not just queued. */
  inArena(): boolean {
    return !!this.duel && this.duel.phase !== 'queue';
  }

  private ladderJoin(id: LadderId, asked?: ActivityMode): void {
    const p = this.w.player;
    const pr = this.w.progress;
    if (this.duel) return this.h.notice('อยู่ในคิวประลองแล้ว');
    if (!LADDERS[id].allows(pr.karma))
      return this.h.notice(`${LADDERS[id].name}สำหรับ${LADDERS[id].side}เท่านั้น`);
    if (p.dead || p.jailT > 0) return this.h.notice('ตอนนี้เข้าร่วมประลองไม่ได้');
    if (p.combatT > 0) return this.h.notice(combatBlockText(p));
    const mode = modesOf(this.w)?.resolve('ladder', asked) ?? 'npc';
    this.duel = {
      mode,
      ladder: id,
      phase: 'queue',
      t: LADDER.queue[0] + this.h.random() * LADDER.queue[1],
      foeId: -1,
      foe: null,
      back: { x: p.x, y: p.y },
      won: false,
      arenaRank: 0,
      autoWas: false,
    };
    this.h.notice(`กำลังจับคู่${LADDERS[id].name}…`);
    this.h.emit({ type: 'ladder', phase: 'queue', ladder: id });
  }

  /** Runs the bout through its phases. */
  step(dt: number): void {
    const d = this.duel;
    if (!d) return;
    const p = this.w.player;
    if (d.phase === 'fight') {
      const foe = this.w.get(d.foeId);
      // Time up: the one with more of their blood left wins.
      if ((d.t -= dt) <= 0) this.endDuel(!foe || p.hp / p.maxHp >= foe.hp / foe.maxHp);
      // An open challenge (systems/duelGround.ts): to a tenth of the health, and out of the ring is out.
      else if (d.open) {
        const lost = p.hp <= p.maxHp * DUEL_GROUND.floorHp || !inRing(d.open.ring, p);
        if (lost || !foe || foe.hp <= foe.maxHp * DUEL_GROUND.floorHp) this.endDuel(!lost);
      }
      return;
    }
    if ((d.t -= dt) > 0) return;
    if (d.phase === 'queue') {
      if (p.dead || p.jailT > 0 || p.combatT > 0) {
        this.duel = null;
        this.h.emit({ type: 'ladder', phase: 'cancel', ladder: d.ladder });
        return this.h.notice('ยกเลิกการจับคู่');
      }
      // Matched: the bout's gear rule (data/pvpTemplate.ts) counts from here, before the foe is scaled to the player.
      d.phase = 'countdown';
      this.w.refreshPlayerStats();
      return this.enterArena(d);
    }
    if (d.phase === 'countdown') {
      d.phase = 'fight';
      d.t = d.ladder ? LADDER.fightSeconds : ARENA.fightSeconds;
      p.targetId = d.foeId;
      // Arena: the player's own fighting AI (auto-fight) takes over, anchored in the ring (not a challenge's).
      if (!d.ladder && !d.open)
        Object.assign(this.w.auto, { on: true, ax: this.w.map.duel.x, ay: this.w.map.duel.y, thinkT: 0 });
      this.h.emit({ type: 'ladder', phase: 'fight', ladder: d.ladder, foe: d.foe!.name });
      return;
    }
    // Result shown: back to where the player was, whole again.
    this.w.entities.delete(d.foeId);
    if (p.targetId === d.foeId) p.targetId = null;
    Object.assign(p, { x: d.back.x, y: d.back.y, z: 0, hp: p.maxHp, qi: p.maxQi, path: null, statuses: [] });
    this.h.input.engage = false;
    this.h.input.block = false;
    if (!d.ladder) this.w.auto.on = d.autoWas;
    this.duel = null;
    this.w.refreshPlayerStats();
  }

  /** Matched: a contender near the player's rating. */
  private enterArena(d: Duel): void {
    const p = this.w.player;
    const id = d.ladder!;
    const st = this.w.progress.ladder[id];
    const field = [...LADDERS[id].contenders].sort(
      (a, b) => Math.abs(a.rating - st.rating) - Math.abs(b.rating - st.rating),
    );
    const c = field[Math.floor(this.h.random() * Math.min(3, field.length))]!;
    const k = contenderStrength(st.rating, c.rating);
    this.bringIn(d, { ...c, title: `${c.title} · ${c.rating}` }, c.rating, {
      maxHp: Math.round(p.maxHp * k),
      hp: Math.round(p.maxHp * k),
      maxQi: p.maxQi,
      qi: p.maxQi,
      atk: p.atk * k,
      def: p.def * k,
      magic: p.magic * k,
      magicDef: p.magicDef * k,
      hit: p.hit,
      magicHit: p.magicHit,
      dodge: p.dodge,
      crit: p.crit,
      magicCrit: p.magicCrit,
      toughness: p.toughness,
      maxStamina: p.maxStamina,
      stamina: p.maxStamina,
    });
    this.h.log('sys', `${LADDERS[id].name}: พบคู่ประลอง ${c.name} (${c.rating})`);
  }

  /** The player and the foe are brought into the ring, facing each other, for the countdown. */
  private bringIn(
    d: Duel,
    who: { name: string; title: string; sprite: string; art: string },
    rating: number,
    stats: Partial<Entity>,
  ): void {
    const p = this.w.player;
    const at = this.w.map.duel;
    this.h.stopMeditating();
    this.h.dismount();
    Object.assign(p, {
      x: at.x - 2.5,
      y: at.y,
      z: 0,
      hp: p.maxHp,
      qi: p.maxQi,
      stamina: p.maxStamina,
      path: null,
      statuses: [],
      cast: null,
    });
    this.h.input.engage = false;
    const foe = this.h.add({
      kind: 'rival',
      x: at.x + 2.5,
      y: at.y,
      name: who.name,
      title: who.title,
      sprite: who.sprite,
      speed: p.speed,
      facing: -1,
      rival: {
        homeX: at.x,
        homeY: at.y,
        aggroT: 0,
        skillT: 1.5,
        skills: getArt(who.art)
          .skills.filter((id) => ['attack', 'break', 'power'].includes(getSkill(id).kind))
          .slice(0, 4),
      },
    });
    Object.assign(foe, stats);
    Object.assign(d, {
      phase: 'countdown',
      t: LADDER.countdown,
      foeId: foe.id,
      foe: { name: who.name, rating },
    });
    this.h.emit({ type: 'ladder', phase: 'countdown', ladder: d.ladder, foe: who.name });
  }

  /** The bout is decided: rating, points, and a few seconds to see it. */
  endDuel(won: boolean): void {
    const d = this.duel!;
    if (d.open) return this.endOpenBout(d, won);
    if (!d.ladder) return this.endArenaBout(d, won);
    const def = LADDERS[d.ladder];
    const st = this.w.progress.ladder[d.ladder];
    const delta = eloDelta(st.rating, d.foe!.rating, won);
    const before = tierAt(def, st.rating);
    st.rating = Math.max(0, st.rating + delta);
    if (won) st.wins++;
    else st.losses++;
    // At most `dailyPointWins` wins a day pay points (the arena's day); the rating moves regardless.
    if (st.day !== this.w.progress.arena.day) Object.assign(st, { day: this.w.progress.arena.day, pointWins: 0 });
    const paid = won && (st.pointWins ?? 0) < LADDER.dailyPointWins;
    if (paid) st.pointWins = (st.pointWins ?? 0) + 1;
    const pts = won ? (paid ? LADDER.points.win : 0) : LADDER.points.loss;
    st.points += pts;
    Object.assign(d, { phase: 'result', t: LADDER.resultSeconds, won });
    const foe = this.w.get(d.foeId);
    if (foe) foe.rival!.aggroT = 0;
    this.h.log(
      'sys',
      `${def.name}: ${won ? 'ชนะ' : 'แพ้'} ${d.foe!.name} · คะแนน ${delta >= 0 ? '+' : ''}${delta} (${st.rating}) · ${def.points} +${pts}`,
    );
    const after = tierAt(def, st.rating);
    if (after > before)
      this.h.notice(`${def.name} ขึ้นสู่${def.tiers[after]!.name} รับรางวัลได้ที่เมนูประลอง (L)`);
    this.h.emit({ type: 'ladder', phase: 'result', ladder: d.ladder, foe: d.foe!.name, won, delta });
    this.h.emit({ type: 'progressChanged' });
  }

  /** An open challenge decided (systems/duelGround.ts): nothing is staked, a few seconds to see it. */
  private endOpenBout(d: Duel, won: boolean): void {
    Object.assign(d, { phase: 'result', t: LADDER.resultSeconds, won });
    const foe = this.w.get(d.foeId);
    if (foe?.rival) foe.rival.aggroT = 0;
    this.h.log('sys', `ท้าประลอง: ${won ? 'ชนะ' : 'แพ้'} ${d.foe!.name}`);
    this.h.emit({ type: 'ladder', phase: 'result', ladder: null, foe: d.foe!.name, won });
  }

  /** A tier's reward, once a season, once its rating has been reached. */
  private ladderClaim(id: LadderId, tier: number): void {
    const def = LADDERS[id];
    const st = this.w.progress.ladder[id];
    const t = def.tiers[tier];
    if (!t || st.claimed.includes(tier)) return;
    if (st.rating < t.rating) return this.h.notice(`ต้องมีคะแนน ${t.rating} ขึ้นไป`);
    st.claimed.push(tier);
    this.h.notice(`${def.name} ${t.name}: ${this.h.grantReward(t.reward)}`);
    this.h.emit({ type: 'progressChanged' });
  }

  /** The ladder shop's fixed-pool gear (data/arenaShop.ts): the buyer picks the affixes; ladder points pay. */
  private ladderBuyGear(id: LadderId, wareId: string, picks: readonly string[]): void {
    const def = LADDERS[id];
    const st = this.w.progress.ladder[id];
    const pr = this.w.progress;
    const good = ARENA_GOOD_BY_ID[wareId];
    if (good) {
      if (st.points < good.price) return this.h.notice(`${def.points}ไม่พอ`);
      st.points -= good.price;
      pr.trades.goods[good.good] = (pr.trades.goods[good.good] ?? 0) + good.n;
      this.h.notice(`แลก「${GOODS[good.good]?.name ?? good.good}」ด้วย${def.points} ${good.price}`);
      return this.h.emit({ type: 'progressChanged' });
    }
    const w = ARENA_GEAR_BY_ID[wareId];
    if (!w || w.ladder !== id) return;
    if (st.points < w.price) return this.h.notice(`${def.points}ไม่พอ`);
    if (pr.inventory.length >= BAG_SIZE) return this.h.notice('ย่ามเต็ม');
    const roll = arenaGearRoll(w, picks);
    if (typeof roll === 'string') return this.h.notice(roll);
    st.points -= w.price;
    const rid = `roll-${pr.nextRoll++}`;
    pr.rolls[rid] = roll;
    pr.inventory.push(rid);
    this.h.notice(`แลก「${ITEMS[w.base]!.name}」ด้วย${def.points} ${w.price}`);
    this.h.emit({ type: 'progressChanged' });
  }

  /** The ladder shop: gear and goods for ladder points. */
  private ladderBuy(id: LadderId, index: number): void {
    const def = LADDERS[id];
    const st = this.w.progress.ladder[id];
    const pr = this.w.progress;
    const w = def.shop[index];
    if (!w) return;
    if (st.points < w.price) return this.h.notice(`${def.points}ไม่พอ`);
    if (w.kind === 'item') {
      if (pr.inventory.length >= BAG_SIZE) return this.h.notice('ย่ามเต็ม');
      st.points -= w.price;
      pr.inventory.push(w.id);
      this.h.notice(`แลก「${ITEMS[w.id]!.name}」ด้วย${def.points} ${w.price}`);
    } else {
      st.points -= w.price;
      this.h.notice(`แลก${w.name}: ${this.h.grantReward(w.reward)}`);
    }
    this.h.emit({ type: 'progressChanged' });
  }
}
